'use client'

import type React from 'react'
import { useCallback, useState } from 'react'
import { cx } from 'utils/cx'
import styles from './Accordion.module.css'
import {
	type AccordionProps,
	AccordionVariant,
	type ElementProps,
} from './Accordion.types'
import { Element } from './Element/Element'

export const Accordion = ({
	data,
	id,
	variant,
	shouldRenderOnExpand = false,
	shouldCollapseOthers = false,
}: AccordionProps): React.ReactNode => {
	const [dataExpanded, setDataExpanded] = useState<boolean[]>(
		data
			.filter((item) => !item.hide)
			.map(({ showExpanded }) => showExpanded ?? false)
	)

	const handleExpand = useCallback(
		(
			index: number,
			onExpandedStart: ElementProps['onExpandedStart'],
			expandId: string,
			setSubtitle: (subtitle: string) => void
		) => {
			if (shouldCollapseOthers) {
				setDataExpanded((prev) => prev.map(() => false))
			}
			setDataExpanded((prev) =>
				prev.map((value, arrayIndex) => (arrayIndex === index ? true : value))
			)
			onExpandedStart?.(expandId, setSubtitle)
		},
		[shouldCollapseOthers]
	)

	const handleCollapse = useCallback(
		(
			index: number,
			onCollapsedStart: ElementProps['onCollapsedStart'],
			collapseId: string,
			setSubtitle: (subtitle: string) => void
		) => {
			setDataExpanded((prev) =>
				prev.map((value, arrayIndex) => (arrayIndex === index ? false : value))
			)
			onCollapsedStart?.(collapseId, setSubtitle)
		},
		[]
	)

	return (
		<div
			className={cx(
				styles.accordion,
				variant === AccordionVariant.Secondary && styles.border
			)}
		>
			{data
				.filter((item) => !item.hide)
				.map(
					(
						{
							title,
							content,
							subtitle,
							onClick,
							dataTestId,
							shouldScroll,
							showCloseIcon,
							onExpanded,
							onCollapsed,
							onExpandedStart,
							onCollapsedStart,
						},
						index
					) => {
						const elementId = `${id}-${index}`
						return (
							<Element
								title={title}
								onExpanded={onExpanded}
								onCollapsed={onCollapsed}
								onExpandedStart={(onExpandedStartId, setSubtitle) => {
									handleExpand(
										index,
										onExpandedStart,
										onExpandedStartId,
										setSubtitle
									)
								}}
								onCollapsedStart={(onCollapsedStartId, setSubtitle) => {
									handleCollapse(
										index,
										onCollapsedStart,
										onCollapsedStartId,
										setSubtitle
									)
								}}
								subtitle={subtitle}
								dataTestId={dataTestId}
								onClick={onClick}
								id={elementId}
								key={elementId}
								expanded={dataExpanded[index]}
								shouldScroll={shouldScroll}
								shouldRenderOnExpand={shouldRenderOnExpand}
								variant={variant}
								showCloseIcon={showCloseIcon}
							>
								{content}
							</Element>
						)
					}
				)}
		</div>
	)
}
